import {
  decimalToPip,
  encodeFixedIncomeVaultConfigurationFields,
  pipToDecimal,
} from '@katanaperps/katana-perps-sdk';
import { ContractTransactionResponse } from 'ethers';

// TODO: vaults revert: import { FixedIncomeVaultProvider_v1 } from 'lib/ethereum/contracts/contractsFromSdk';
import { FixedIncomeVaultProvider_v1 } from 'lib/ethereum/contracts/contractsFromSdk';
import { trimTo8Decimals } from 'lib/numbers';
import { IWallet } from 'reduxState/wallet/walletReducer';

import {
  getVaultProviderContract,
  getKatanaWalletSigner,
} from './vaultsContract';

/**
 * @params Interest Multiplier in in PIPs
 * @returns APY must by multiplied by 100 to get % value
 */
export const convertVaultInterestMultiplierToAPY = (
  interestMultiplier: bigint
) => {
  return Math.exp(Number(pipToDecimal(interestMultiplier))) - 1;
};

export function convertVaultApyToInterestMultiplierInPips(
  apyPercent: number | string
): bigint {
  return decimalToPip(trimTo8Decimals(Math.log(1 + Number(apyPercent) / 100)));
}

export type VaultTransformedConfig = {
  // TODO: vaults revert: originalFields: FixedIncomeVaultProvider_v1.VaultConfigurationFieldsStructOutput;
  originalFields: FixedIncomeVaultProvider_v1.VaultConfigurationFieldsStructOutput;
  managerWallet: string;
  effectiveTimestampInS: bigint;
  interestMultiplier: string;
  apy: number;
  maximumNetDeposits: string;
  maximumTotalOwedQuantityAvailableMultiplierToInitiateExit: string;
  minimumTotalOwedQuantityAvailableMultiplierToAllowManagerWalletWithdrawal: string;
  minimumUnappliedDepositOrWithdrawalAgeInSToInitiateExit: bigint;
  withdrawalLimitPercentForDepositors: string;
  /** Once the vault-level limit gets used up across depositors, then it goes to individual depositor-level limits */
  withdrawalLimitPercentForVault: string;
};

/**
 * Apply transformations for easier usage on the UI & less computation
 * APY calculation
 *  - https://docs.google.com/spreadsheets/d/1af23exVIHmqagqJ4c1sXkQ_8M2nMIcXgZWiF962Lnkc/edit?gid=932525904#gid=932525904
 *  - based on interest multiplier expecting it to be for 365 days (by default)
 */
export function transformConfigurationFields(
  fieldsRaw: FixedIncomeVaultProvider_v1.VaultConfigurationFieldsStructOutput
): VaultTransformedConfig {
  // Explicitly pick named properties to ensure a plain object (ethers Result is a tuple + named props)
  const fields: FixedIncomeVaultProvider_v1.VaultConfigurationFieldsStructOutput =
    {
      managerWallet: fieldsRaw.managerWallet,
      effectiveTimestampInS: fieldsRaw.effectiveTimestampInS,
      interestMultiplier: fieldsRaw.interestMultiplier,
      maximumNetDeposits: fieldsRaw.maximumNetDeposits,
      maximumTotalOwedQuantityAvailableMultiplierToInitiateExit:
        fieldsRaw.maximumTotalOwedQuantityAvailableMultiplierToInitiateExit,
      minimumTotalOwedQuantityAvailableMultiplierToAllowManagerWalletWithdrawal:
        fieldsRaw.minimumTotalOwedQuantityAvailableMultiplierToAllowManagerWalletWithdrawal,
      minimumUnappliedDepositOrWithdrawalAgeInSToInitiateExit:
        fieldsRaw.minimumUnappliedDepositOrWithdrawalAgeInSToInitiateExit,
      withdrawalLimitPercentForDepositors:
        fieldsRaw.withdrawalLimitPercentForDepositors,
      withdrawalLimitPercentForVault: fieldsRaw.withdrawalLimitPercentForVault,
    } as FixedIncomeVaultProvider_v1.VaultConfigurationFieldsStructOutput;
  return {
    // TODO: vaults revert: originalFields: fields,
    originalFields: fields,

    managerWallet: fields.managerWallet,
    effectiveTimestampInS: fields.effectiveTimestampInS,
    /** APY 0.01 = 1% */
    interestMultiplier: pipToDecimal(fields.interestMultiplier),
    // const result = Math.exp(B24 * (B27 / B26)) - 1;
    apy: convertVaultInterestMultiplierToAPY(fields.interestMultiplier),
    maximumNetDeposits: pipToDecimal(fields.maximumNetDeposits),
    /** 0.80 = 80% */
    maximumTotalOwedQuantityAvailableMultiplierToInitiateExit: pipToDecimal(
      fields.maximumTotalOwedQuantityAvailableMultiplierToInitiateExit
    ),
    /** 1.10 = 110% */
    minimumTotalOwedQuantityAvailableMultiplierToAllowManagerWalletWithdrawal:
      pipToDecimal(
        fields.minimumTotalOwedQuantityAvailableMultiplierToAllowManagerWalletWithdrawal
      ),
    /** 3600s = 1 hour */
    minimumUnappliedDepositOrWithdrawalAgeInSToInitiateExit:
      fields.minimumUnappliedDepositOrWithdrawalAgeInSToInitiateExit,
    /** Withdrawal rate, 0.2 = 20% */
    withdrawalLimitPercentForDepositors: pipToDecimal(
      fields.withdrawalLimitPercentForDepositors
    ),
    withdrawalLimitPercentForVault: pipToDecimal(
      fields.withdrawalLimitPercentForVault
    ),
    // Multiply maximumTotalOwedQuantityAvailableMultiplierToInitiateExit by totalOwedQuantity (and divide by 10^8) to get exitTreshold
    // withdrawalLimitPercent is the withdrawal rate
    // withdrawal treshold = minimumTotalOwedQuantityAvailableMultiplierToAllowManagerWalletWithdrawal * totalOwedQuantity / 10^8
  };
}

export type VaultConfigFormParams = {
  apyPercent: string;
  maximumNetDeposits: string | number;
  withdrawalLimitPercentForDepositors: string | number;
  withdrawalLimitPercentForVault: string | number;
  withdrawalTresholdPercent: string | number;
  exitTresholdPercent: string | number;
  withdrawalExitAgeInHours: string | number;
};

/** Convert form-shaped UI values into the binary payload accepted by the
 *  FixedIncomeVaultProvider contract. Shared by creation and settings update. */
export function encodeVaultConfigPayload({
  managerWallet,
  effectiveTimestampInS,
  params,
}: {
  managerWallet: string;
  effectiveTimestampInS: number;
  params: VaultConfigFormParams;
}) {
  return encodeFixedIncomeVaultConfigurationFields({
    managerWallet,
    effectiveTimestampInS,
    interestMultiplierInPips: convertVaultApyToInterestMultiplierInPips(
      params.apyPercent
    ),
    maximumNetDepositsInPips: decimalToPip(
      trimTo8Decimals(params.maximumNetDeposits)
    ),
    maximumTotalOwedQuantityAvailableForExitWithdrawalMultiplierNeededToInitiateExitInPips:
      decimalToPip(trimTo8Decimals(Number(params.exitTresholdPercent) / 100)),
    minimumTotalOwedQuantityAvailableForExitWithdrawalMultiplierToAllowManagerWalletWithdrawalInPips:
      decimalToPip(
        trimTo8Decimals(Number(params.withdrawalTresholdPercent) / 100)
      ),
    minimumUnappliedWithdrawalAgeInSNeededToInitiateExit: Math.floor(
      Number(params.withdrawalExitAgeInHours) * 3600
    ),
    withdrawalLimitPercentForDepositorsInPips: decimalToPip(
      trimTo8Decimals(Number(params.withdrawalLimitPercentForDepositors) / 100)
    ),
    withdrawalLimitPercentForVaultInPips: decimalToPip(
      trimTo8Decimals(Number(params.withdrawalLimitPercentForVault) / 100)
    ),
  });
}

export async function initiateConfigurationChange(
  managerWallet: string,
  updatedParams: VaultConfigFormParams,
  wallet: IWallet
) {
  const signer = await getKatanaWalletSigner(wallet);

  const configUpdateDelayS = await fetchConfigUpdateDelayS();
  const dateNowS = Math.floor(Date.now() / 1000);
  const effectiveTimestampInS = dateNowS + Number(configUpdateDelayS);

  const payload = encodeVaultConfigPayload({
    managerWallet,
    effectiveTimestampInS,
    params: updatedParams,
  });

  // const maximumNetDeposits = updatedParams.maximumNetDeposits
  //   ? decimalToPip(trimTo8Decimals(updatedParams.maximumNetDeposits))
  //   : fallbackParams.maximumNetDeposits;
  // const maximumTotalOwedQuantityAvailableMultiplierToInitiateExit =
  //   updatedParams.exitTresholdPercent
  //     ? decimalToPip(
  //         trimTo8Decimals(Number(updatedParams.exitTresholdPercent) / 100)
  //       )
  //     : fallbackParams.maximumTotalOwedQuantityAvailableMultiplierToInitiateExit;
  // const minimumTotalOwedQuantityAvailableMultiplierToAllowManagerWalletWithdrawal =
  //   updatedParams.withdrawalTresholdPercent
  //     ? decimalToPip(
  //         trimTo8Decimals(Number(updatedParams.withdrawalTresholdPercent) / 100)
  //       )
  //     : fallbackParams.minimumTotalOwedQuantityAvailableMultiplierToAllowManagerWalletWithdrawal;
  // const withdrawalLimitPercent = updatedParams.withdrawalLimitPercent
  //   ? decimalToPip(
  //       trimTo8Decimals(Number(updatedParams.withdrawalLimitPercent) / 100)
  //     )
  //   : fallbackParams.withdrawalLimitPercent;
  // const paramsList = [
  //   managerWallet,
  //   effectiveTimestampInS,
  //   interestMultiplier,
  //   maximumNetDeposits,
  //   maximumTotalOwedQuantityAvailableMultiplierToInitiateExit,
  //   minimumTotalOwedQuantityAvailableMultiplierToAllowManagerWalletWithdrawal,
  //   // TODO: vaults - configurable ?
  //   fallbackParams.minimumUnappliedWithdrawalAgeInSToInitiateExit,
  //   withdrawalLimitPercent,
  // ];
  console.log('Changed fields: ', updatedParams);
  // console.log('Persisted object: ', fallbackParams);
  console.log('Vaults config payload', payload);

  return getVaultProviderContract(signer).initiateManagedAccountUpgrade(
    payload
  );
}

export async function finalizeManagedAccountUpgrade(
  wallet: IWallet
): Promise<ContractTransactionResponse> {
  const signer = await getKatanaWalletSigner(wallet);
  return getVaultProviderContract(signer).finalizeManagedAccountUpgrade();
}

export async function cancelManagedAccountUpgrade(
  wallet: IWallet
): Promise<ContractTransactionResponse> {
  const signer = await getKatanaWalletSigner(wallet);
  return getVaultProviderContract(signer).cancelManagedAccountUpgrade();
}

function fetchConfigUpdateDelayS(): Promise<bigint> {
  return getVaultProviderContract().managedAccountUpgradeBlockTimestampDelayInS();
}

// export function fetchVaultConfigurationUpgrades(managerWallet: string) {
//   return getVaultProviderContract().vaultConfigurationUpgradesByManagerWallet(
//     managerWallet
//   );
// }

export async function setVaultDeposits(enabled: boolean, wallet: IWallet) {
  const signer = await getKatanaWalletSigner(wallet);
  return getVaultProviderContract(signer).setDepositEnabled(enabled);
}
